import { sortRoomsByPriority } from 'config/Constants';
import { format } from 'date-fns';
import { useInterval } from 'hooks/usePolling';
import { usePpsAccess } from 'hooks/usePpsAccess';
import { useStore } from 'hooks/useStore';
import {
  CleanCalculationsResponse,
  ProductCalculationsResponse,
  ScheduleCalculationsResponse
} from 'Interfaces/CalculationInterfaces/MainCalculationInterfaces';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { bottleByRoomCalculationsServices } from 'services/CalculationServices/bottlebyroomCalculationsServices';
import { cleanByRoomService } from 'services/CalculationServices/cleanByRoomService';
import { cleanCalculationServices } from 'services/CalculationServices/cleanCalculationsServices';
import { hourlyBPHbyRoomService } from 'services/CalculationServices/hourlyBPHbyRoomService';
import { hourlyScheduleByRoomService } from 'services/CalculationServices/hourlyScheduleByRoomService';
import { productCalculationServices } from 'services/CalculationServices/productCalsServices';
import { scheduleCalculationServices } from 'services/CalculationServices/scheduleCalculationsServices';
import { getJitteredInterval } from 'utils/pollingUtility';
import { BootleByRoomDataInterface } from './components/BottleByRoomCalculationTable/bottlebyroom.types';
import { RoomData } from './components/HourlyBPHByRoom/hourlybphByRoom.type';
import { HourlyScheduleByRoomData } from './components/HourlyScheduleByRoomTable/hourlyScheduleByRoom.types';

// Constants
const TAB_INDICES = {
  PRODUCTS: 0,
  CLEAN: 1,
  SCHEDULE: 2,
  CLEAN_BY_ROOM: 3,
  BOTTLE_BY_ROOM: 4,
  HOURLY_BPH_BY_ROOM: 5,
  HOURLY_SCHEDULE_BY_ROOM: 6
} as const;

// Types
type TabIndex = typeof TAB_INDICES[keyof typeof TAB_INDICES];

interface CalculationData {
  productData: ProductCalculationsResponse;
  cleanData: CleanCalculationsResponse;
  scheduleData: ScheduleCalculationsResponse;
  bottleByRoomData: BootleByRoomDataInterface[];
  cleanByRoomData: {}[];
  hourlyBPHByRoomData: RoomData[];
  hourlyScheduleByRoomData: HourlyScheduleByRoomData[];
}

interface FetchConfig {
  service: (date: string) => Promise<any>;
  dataKey: keyof CalculationData;
  requiresRoomInclusion?: boolean;
  requiresSorting?: boolean;
}

// Helper functions
const getFormattedDate = (date: Date | null): string => {
  return date ? format(date, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
};

const parseTabFromHash = (hash: string): number | null => {
  const tabIndex = parseInt(hash.replace('#', ''), 10);
  return isNaN(tabIndex) ? null : tabIndex;
};

const createRoomDataFromStore = (storeRoom: any) => ({
  id: storeRoom.id,
  name: storeRoom.room_long,
  shortCode: storeRoom.room_short,
  calculations: []
});

const sortDataByRoomName = <T extends { room?: string; room_name?: string }>(data: T[]): T[] => {
  return [...data].sort((a, b) => {
    const roomA = a.room || a.room_name || '';
    const roomB = b.room || b.room_name || '';
    return roomA.localeCompare(roomB);
  });
};

const updateCalculationData = (
  setCalculationData: React.Dispatch<React.SetStateAction<CalculationData>>,
  key: keyof CalculationData,
  data: any
) => {
  setCalculationData(prev => ({ ...prev, [key]: data }));
};

// Fetch configuration mapping
const FETCH_CONFIGS: Record<TabIndex, FetchConfig> = {
  [TAB_INDICES.PRODUCTS]: {
    service: productCalculationServices.getProductCalculations,
    dataKey: 'productData',
    requiresRoomInclusion: true
  },
  [TAB_INDICES.CLEAN]: {
    service: cleanCalculationServices.getCleanCalculations,
    dataKey: 'cleanData',
    requiresRoomInclusion: true
  },
  [TAB_INDICES.SCHEDULE]: {
    service: scheduleCalculationServices.getScheduleCalculations,
    dataKey: 'scheduleData',
    requiresRoomInclusion: true
  },
  [TAB_INDICES.CLEAN_BY_ROOM]: {
    service: cleanByRoomService.getCleanByRoomData,
    dataKey: 'cleanByRoomData'
  },
  [TAB_INDICES.BOTTLE_BY_ROOM]: {
    service: bottleByRoomCalculationsServices.getBottleByRoom,
    dataKey: 'bottleByRoomData',
    requiresSorting: true
  },
  [TAB_INDICES.HOURLY_BPH_BY_ROOM]: {
    service: hourlyBPHbyRoomService.getHourlyBPHbyRoom,
    dataKey: 'hourlyBPHByRoomData',
    requiresSorting: true
  },
  [TAB_INDICES.HOURLY_SCHEDULE_BY_ROOM]: {
    service: hourlyScheduleByRoomService.getHourlyScheduleByRoom,
    dataKey: 'hourlyScheduleByRoomData',
    requiresSorting: true
  }
};

export const useCalculationScreen = () => {
  // State management
  const [date, setDate] = useState<Date | null>(new Date());
  const [activeIndex, setActiveIndex] = useState<TabIndex>(TAB_INDICES.PRODUCTS);
  const [expandedRows, setExpandedRows] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [roomsLoaded, setRoomsLoaded] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Data state
  const [calculationData, setCalculationData] = useState<CalculationData>({
    productData: [],
    cleanData: [],
    scheduleData: [],
    bottleByRoomData: [],
    cleanByRoomData: [],
    hourlyBPHByRoomData: [],
    hourlyScheduleByRoomData: []
  });

  // Hooks
  const { roomStore } = useStore();
  const location = useLocation();
  const navigate = useNavigate();
  const { canViewProductCalculations, canViewCleanCalculations, canViewScheduleCalculations } =
    usePpsAccess();

  const POLLING_DELAY = getJitteredInterval();

  // Room loading logic
  const loadRooms = useCallback(async () => {
    if (roomStore.rooms.length === 0) {
      await roomStore.fetchRooms(1, 100);
    }
    setRoomsLoaded(true);
  }, [roomStore]);

  // Ensure all rooms are included in data
  const ensureAllRoomsIncluded = useCallback(
    (responseData: any[]): any[] => {
      if (!roomsLoaded || roomStore.rooms.length === 0) {
        return responseData;
      }

      const existingRoomIds = new Map(responseData.map(room => [room.id, true]));
      const allRooms = [...responseData];

      roomStore.rooms.forEach(storeRoom => {
        if (!existingRoomIds.has(storeRoom.id)) {
          allRooms.push(createRoomDataFromStore(storeRoom));
        }
      });

      return sortRoomsByPriority(allRooms);
    },
    [roomStore.rooms, roomsLoaded]
  );

  // Generic fetch function
  const fetchDataForTab = useCallback(
    async (tabIndex: TabIndex, formattedDate: string) => {
      const config = FETCH_CONFIGS[tabIndex];
      if (!config) return;

      try {
        const response = await config.service(formattedDate);
        let processedData = response;

        if (response.success && response.data) {
          processedData = response.data;
        }

        if (config.requiresSorting) {
          processedData = sortDataByRoomName(processedData);
        }

        if (config.requiresRoomInclusion) {
          processedData = ensureAllRoomsIncluded(processedData);
        }

        updateCalculationData(setCalculationData, config.dataKey, processedData);
      } catch (error: any) {
        if (config.requiresRoomInclusion) {
          const fallbackData = ensureAllRoomsIncluded([]);
          updateCalculationData(setCalculationData, config.dataKey, fallbackData);
        }
      } finally {
        setLoading(false);
      }
    },
    [ensureAllRoomsIncluded]
  );

  // Main data fetching function
  const fetchData = useCallback(
    async (isPollingRefresh = false) => {
      if (!isPollingRefresh) {
        setLoading(true);
      }

      const formattedDate = getFormattedDate(date);

      if (!roomsLoaded) {
        await loadRooms();
      }

      await fetchDataForTab(activeIndex, formattedDate);

      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    },
    [date, activeIndex, roomsLoaded, loadRooms, fetchDataForTab, isInitialLoad]
  );

  // Clear data for specific tab
  const clearDataForTab = useCallback((tabIndex: TabIndex) => {
    const config = FETCH_CONFIGS[tabIndex];
    if (config) {
      updateCalculationData(setCalculationData, config.dataKey, []);
    }
  }, []);

  // Tab change handler
  const handleTabChange = useCallback((e: { index: number }) => {
    const newIndex = e.index as TabIndex;
    if (newIndex === activeIndex) {
      return;
    }

    setActiveIndex(newIndex);
    navigate(`#${newIndex}`, { replace: true });

    setLoading(true);
    setIsInitialLoad(true);

    clearDataForTab(newIndex);
  }, [activeIndex, navigate, clearDataForTab]);

  // Effects
  useEffect(() => {
    loadRooms();
  }, [loadRooms]);

  useEffect(() => {
    const tabIndex = parseTabFromHash(location.hash);
    if (tabIndex !== null && tabIndex !== activeIndex) {
      setActiveIndex(tabIndex as TabIndex);
    }
  }, [location.hash, activeIndex]);

  useEffect(() => {
    if (roomsLoaded) {
      fetchData(false);
    }
  }, [fetchData, roomsLoaded]);

  useInterval(
    () => {
      if (roomsLoaded && !loading) {
        fetchData(true);
      }
    },
    roomsLoaded ? POLLING_DELAY : null
  );

  return {
    // State
    date,
    setDate,
    activeIndex,
    expandedRows,
    setExpandedRows,
    ...calculationData,
    loading,
    roomsLoaded,

    // Functions
    handleTabChange,

    // Permissions
    canViewProductCalculations,
    canViewCleanCalculations,
    canViewScheduleCalculations
  };
};
